import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Activity, FileText, Pill, BookOpen, LayoutDashboard, Menu, X, Apple, Sparkles, Heart, GraduationCap, Lightbulb, ChevronDown, ChevronRight, Bell, Download, Compass, HelpCircle, BookMarked, Eye, Newspaper } from 'lucide-react';
import PreviewBanner from './PreviewBanner';
import Footer from './Footer';
import Seo from './Seo';
import { getNavColors } from '../config/navigationColors';

const PREVIEW_TITLES = {
  '/preview/dashboard': 'Dashboard Preview',
  '/preview/blood-results': 'Blood Results Preview',
  '/preview/nutrition': 'Nutrition Tracking Preview',
  '/preview/symptoms': 'Symptom Tracking Preview',
  '/preview/vitamins': 'Treatments Preview',
  '/preview/integrative-health': 'Integrative Health Preview',
  '/preview/vitamin-d': 'Vitamin D Preview',
  '/preview/education': 'Education Preview',
  '/preview/learn-notes': 'Learn & Notes Preview',
  '/preview/my-ideas': 'My Ideas Preview',
  '/preview/recipes': 'Recipes Preview',
  '/preview/smoothies': 'Smoothies Preview',
  '/preview/eating-well': 'Eating Well Preview',
  '/preview/food-safety': 'Food Safety Preview',
};

/**
 * Preview Layout Component
 * Read-only layout for preview mode - no auth required
 */
const PreviewLayout = ({ children }) => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState(['integrative-health', 'education']);

  // Add preview class to body
  useEffect(() => {
    document.body.classList.add('preview-layout');
    return () => document.body.classList.remove('preview-layout');
  }, []);

  const toggleMenu = (menuId) => {
    setExpandedMenus(prev => 
      prev.includes(menuId) 
        ? prev.filter(id => id !== menuId)
        : [...prev, menuId]
    );
  };

  // Preview navigation items - matching production app structure
  // Expandable sections can be clicked to expand, but sub-items are disabled
  const navItems = [
    { path: '/preview/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/preview/how-to-use', label: 'How to Use', icon: Compass, disabled: true },
    { path: '/preview/blood-results', label: 'My Progress', icon: Activity },
    { path: '/preview/vitamins', label: 'Treatments', icon: Pill },
    { path: '/preview/nutrition', label: 'Nutrition & Deficiencies', icon: Apple },
    { path: '/preview/symptoms', label: 'Symptoms', icon: FileText },
    { 
      id: 'integrative-health',
      path: '/preview/integrative-health', 
      label: 'Integrative Health', 
      icon: Heart,
      expandable: true,
      subItems: [
        { path: '#', label: 'Vitamin D', disabled: true },
        { path: '#', label: 'Stress & Mental Wellbeing', disabled: true },
        { path: '#', label: 'Sleep & Recovery', disabled: true },
        { path: '#', label: 'Vitamin C (IV)', disabled: true },
        { path: '/preview/recipes', label: 'Recipes' },
        { path: '/preview/smoothies', label: 'Smoothies' },
        { path: '/preview/eating-well', label: 'Eating Well With CLL' },
        { path: '/preview/food-safety', label: 'Food Safety' }
      ]
    },
    { 
      id: 'education',
      path: '/preview/education', 
      label: 'Information & Education', 
      icon: GraduationCap,
      expandable: true,
      subItems: [
        { path: '#', label: 'What Is CLL', disabled: true },
        { path: '#', label: 'Watch and Wait', disabled: true },
        { path: '#', label: 'Blood Markers', disabled: true }
      ]
    },
    { path: '/preview/reading-learning', label: 'Reading & Learning', icon: BookMarked, disabled: true },
    { path: '/preview/common-questions', label: 'Common Questions', icon: HelpCircle, disabled: true },
    { path: '/blog', label: 'Blog', icon: Newspaper },
    { path: '/preview/learn-notes', label: 'Learn & Notes', icon: BookOpen },
    { path: '/preview/glossary', label: 'Glossary', icon: BookMarked, disabled: true },
    { path: '/preview/alerts', label: 'Alerts', icon: Bell, disabled: true },
    { path: '/preview/my-ideas', label: 'My Ideas & Research', icon: Lightbulb },
    { path: '/preview/export', label: 'Export', icon: Download, disabled: true }
  ];

  const renderNavItem = (item) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.path;
    const hasSubItems = item.subItems && item.subItems.length > 0;
    const isExpanded = expandedMenus.includes(item.id);
    const isSubItemActive = hasSubItems && item.subItems.some(sub => location.pathname === sub.path);
    const colors = getNavColors(item.path.replace('/preview', ''));
    const isDisabled = item.disabled;
    const isExpandable = item.expandable; // Can expand to show sub-items, but sub-items disabled

    // Expandable items (clickable parent with page, disabled sub-items) - e.g., Integrative Health, Information & Education
    if (isExpandable && hasSubItems) {
      const isParentActive = location.pathname === item.path;
      return (
        <div key={item.path || item.id}>
          <div className="flex items-center">
            <Link
              to={item.path}
              onClick={() => {
                if (!expandedMenus.includes(item.id)) {
                  toggleMenu(item.id);
                }
                setMobileMenuOpen(false);
              }}
              className={`flex-1 flex items-center px-4 py-3 text-base rounded-l-full transition-colors ${
                isParentActive || isSubItemActive
                  ? 'font-medium'
                  : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
              }`}
              style={(isParentActive || isSubItemActive) ? { backgroundColor: colors.activeBg } : {}}
            >
              <Icon 
                className="h-5 w-5 mr-3" 
                style={{ color: (isParentActive || isSubItemActive) ? colors.activeIcon : colors.icon }}
              />
              <span className={(isParentActive || isSubItemActive) ? 'text-slate-900' : ''}>
                {item.label}
              </span>
            </Link>
            <button
              onClick={() => toggleMenu(item.id)}
              className={`px-3 py-3 rounded-r-full transition-colors ${
                isParentActive || isSubItemActive
                  ? ''
                  : 'text-slate-400 hover:bg-slate-50 hover:text-slate-600'
              }`}
              style={(isParentActive || isSubItemActive) ? { backgroundColor: colors.activeBg } : {}}
            >
              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          </div>
          {isExpanded && (
            <div className="ml-8 mt-1 space-y-1">
              {item.subItems.map((subItem, idx) => {
                // If subItem is disabled or has # path, show disabled state
                if (subItem.disabled || subItem.path === '#') {
                  return (
                    <div
                      key={subItem.path || idx}
                      className="flex items-center px-4 py-2 text-sm rounded-full text-slate-400 cursor-not-allowed"
                    >
                      <span className="w-1.5 h-1.5 rounded-full mr-3 bg-slate-200"></span>
                      {subItem.label}
                    </div>
                  );
                }
                // Otherwise, show clickable link
                const isSubActive = location.pathname === subItem.path;
                return (
                  <Link
                    key={subItem.path}
                    to={subItem.path}
                    className={`flex items-center px-4 py-2 text-sm rounded-full transition-colors ${
                      isSubActive
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full mr-3 ${isSubActive ? 'bg-primary' : 'bg-slate-300'}`}></span>
                    {subItem.label}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    // Simple disabled items (no sub-items)
    if (isDisabled) {
      return (
        <div key={item.path} className="flex items-center px-4 py-3 text-base rounded-full text-slate-400 cursor-not-allowed">
          <Icon className="h-5 w-5 mr-3 opacity-50" />
          <span className="opacity-70">{item.label}</span>
        </div>
      );
    }

    return (
      <div key={item.path}>
        {hasSubItems ? (
          <>
            <div className="flex items-center">
              <Link
                to={item.path}
                className={`flex-1 flex items-center px-4 py-3 text-base rounded-l-full transition-colors ${
                  isActive || isSubItemActive
                    ? 'font-medium'
                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                }`}
                style={(isActive || isSubItemActive) ? { backgroundColor: colors.activeBg } : {}}
                onClick={() => setMobileMenuOpen(false)}
              >
                <Icon 
                  className="h-5 w-5 mr-3" 
                  style={{ color: (isActive || isSubItemActive) ? colors.activeIcon : colors.icon }}
                />
                <span className={(isActive || isSubItemActive) ? 'text-slate-900' : ''}>
                  {item.label}
                </span>
              </Link>
              <button
                onClick={() => toggleMenu(item.id)}
                className={`px-3 py-3 rounded-r-full transition-colors ${
                  isActive || isSubItemActive
                    ? ''
                    : 'text-slate-400 hover:bg-slate-50 hover:text-slate-600'
                }`}
                style={(isActive || isSubItemActive) ? { backgroundColor: colors.activeBg } : {}}
              >
                {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
            </div>
            {isExpanded && (
              <div className="ml-8 mt-1 space-y-1">
                {item.subItems.map(subItem => {
                  const isSubActive = location.pathname === subItem.path;
                  return (
                    <Link
                      key={subItem.path}
                      to={subItem.path}
                      className={`flex items-center px-4 py-2 text-sm rounded-full transition-colors ${
                        isSubActive
                          ? 'bg-primary/10 text-primary font-medium'
                          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                      }`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <span className={`w-1.5 h-1.5 rounded-full mr-3 ${isSubActive ? 'bg-primary' : 'bg-slate-300'}`}></span>
                      {subItem.label}
                    </Link>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          <Link
            to={item.path}
            className={`flex items-center px-4 py-3 text-base rounded-full transition-colors ${
              isActive
                ? 'font-medium'
                : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
            }`}
            style={isActive ? { backgroundColor: colors.activeBg } : {}}
            onClick={() => setMobileMenuOpen(false)}
          >
            <Icon 
              className="h-5 w-5 mr-3" 
              style={{ color: isActive ? colors.activeIcon : colors.icon }}
            />
            <span className={isActive ? 'text-slate-900' : ''}>
              {item.label}
            </span>
          </Link>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <Seo
        title={`${PREVIEW_TITLES[location.pathname] || 'Interactive Preview'} – MyCBC`}
        description="Explore MyCBC with sample data — no account needed. See how CLL/SLL patients track blood results, symptoms, treatments and nutrition."
        path={location.pathname}
      />
      {/* Desktop Sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-72 lg:flex-col">
        <div className="flex flex-col h-full bg-white border-r border-slate-100 pt-6 pb-4">
          <div className="flex-shrink-0 px-6 mb-6">
            <div className="flex items-center mb-1">
              <Activity className="h-8 w-8 text-blue-600" />
              <span className="ml-3 text-xl font-bold text-blue-600">MyCBC</span>
            </div>
            <p className="text-sm text-slate-500 ml-11">Your health tracking overview</p>
          </div>
          
          <nav className="flex-1 px-4 space-y-1 overflow-y-auto">
            {navItems.map(renderNavItem)}
          </nav>

          {/* Sign Up CTA */}
          <div className="px-4 mt-4 pt-4 border-t border-slate-100">
            <Link
              to="/#plans"
              className="flex items-center justify-center gap-2 px-4 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors"
            >
              Start your free 30 days
            </Link>
            <Link
              to="/login"
              className="flex items-center justify-center gap-2 px-4 py-2 mt-2 text-slate-600 hover:text-primary text-sm"
            >
              Already have an account? Sign in
            </Link>
          </div>
        </div>
      </aside>

      {/* Mobile Header - includes preview banner on mobile */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50">
        {/* Preview Banner - mobile version */}
        <div className="bg-amber-50 border-b border-amber-200 px-3 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-amber-700 text-xs">
              <Eye className="h-3.5 w-3.5" />
              <span className="font-medium">Preview mode</span>
            </div>
            <Link
              to="/"
              className="flex items-center gap-1 px-2 py-1 bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium rounded transition-colors"
            >
              <X className="h-3 w-3" />
              Exit
            </Link>
          </div>
        </div>
        {/* Navigation header */}
        <div className="bg-white border-b border-slate-100">
          <div className="flex items-center justify-between px-4 py-2">
            <div className="flex items-center">
              <Activity className="h-5 w-5 text-blue-600" />
              <span className="ml-1.5 text-base font-bold text-blue-600">MyCBC</span>
            </div>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-1.5 rounded-lg hover:bg-slate-50"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-[60] bg-white flex flex-col" style={{ paddingTop: '128px' }}>
          <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
            {navItems.map(renderNavItem)}
          </nav>
          <div className="flex-shrink-0 px-4 py-4 border-t border-slate-100 bg-white">
            <Link
              to="/#plans"
              className="flex items-center justify-center gap-2 px-4 py-3 bg-primary text-white rounded-lg font-medium"
              onClick={() => setMobileMenuOpen(false)}
            >
              Start your free 30 days
            </Link>
            <Link
              to="/login"
              className="flex items-center justify-center gap-2 px-4 py-2 mt-2 text-slate-600 hover:text-primary text-sm"
              onClick={() => setMobileMenuOpen(false)}
            >
              Already have an account? Sign in
            </Link>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="lg:pl-72">
        {/* Desktop-only preview banner - fixed at top */}
        <div className="hidden lg:block fixed top-0 left-72 right-0 z-50">
          <PreviewBanner />
        </div>
        <div className="pt-20 lg:pt-12">
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {children}
          </div>
        </div>
        <Footer />
      </main>
    </div>
  );
};

export default PreviewLayout;
